import { compareWithAppVersion, ZpayRelatedItem } from '@croquiscom/web2app';

import { ZpayEvent } from '@common/app-manager';
import { ZigzagEvent as ZigzagLogEvent } from '@common/log-manager';
import { useInitLikedProducts, useLikeProductService } from '@widgets/like-product';
import { useGetReviewEventBannerByProductIdQuery } from '@widgets/review-event-banner';
import {
  addZzSessionData,
  getZzSessionData,
  PdpBrowsingType,
  PdpCatalogPaymentType,
  PdpShippingFeeType,
  TabType,
  useCatalogProductIdParams,
  useGetPdpIntegratedData,
  usePdpBaseInfo,
  usePdpConfig,
  usePdpRelatedProductRecommendGroups,
  useProductPreviewAtiveTabType,
  useProductReviewTotalCount,
  useProductThumbnail,
  useSavedMyGoods,
} from '@domains/pdp-common';

import { useSubButtonLog } from '../log/useSubButtonLog';

const MY_GOODS_COUNT_SESSION_DATA_KEY = 'my_goods_count';

export const useInAppSubButtonsService = () => {
  useSavedMyGoods();
  const params = useCatalogProductIdParams();
  const catalog_product_id = params.catalog_product_id ?? '';
  const { data } = useGetReviewEventBannerByProductIdQuery({ catalogProductId: catalog_product_id });
  const { review_prediction_rating, enabledPredictionRating, isPredictSuccessed, isLoading } = useGetPdpIntegratedData({
    catalog_product_id,
  });
  const log = useSubButtonLog();
  const { url } = useProductThumbnail();

  const {
    product,
    shop,
    flags: { is_selected_shop },
  } = usePdpBaseInfo(params);

  useInitLikedProducts({
    data: [
      {
        catalog_product_id,
        is_saved_product: product.is_saved_product,
      },
    ],
    convert: (data) => data.map((value) => value.catalog_product_id),
  });

  const { has_recommend_items } = usePdpRelatedProductRecommendGroups({
    catalog_product_id: params.catalog_product_id,
    isSelectedShop: Boolean(is_selected_shop),
  }); // PDP 본문에서 미리 불러온 recommendGroup 데이터를 재활용 (usePdpRecommendSize를 호출하면 이중호출 발생)
  const { review_total_count } = useProductReviewTotalCount(params.catalog_product_id);

  const { path, isProductPreview } = usePdpConfig();
  const { onInAppLikeProduct } = useLikeProductService();

  const [_, setProductPreviewActiveTabType] = useProductPreviewAtiveTabType();

  // MEMO: 서버 레이턴시 이슈로 임시 제거
  // const { isExternalProductSellable, isFetched: SellableFetched } = useIsExternalProductSellable();

  const country = 'KR';
  const isKorea = country === 'KR';

  const getBottomRelatedButtonData = () => {
    if (isKorea && compareWithAppVersion('7.18.0') && has_recommend_items) {
      return {
        buttonTitle: '연관상품 더 보기',
        buttonAction: `window.zigzagHandler.onSavedMyGoods(${TabType.RECOMMEND});`,
      };
    } else if (compareWithAppVersion('7.18.0')) {
      return {
        buttonTitle: null,
        buttonAction: null,
      };
    }
    return {};
  };

  const sendSavedProductBrazeLog = () => {
    // BRAZE LOG: 동 세션에서 찜 추가 3회 이상의 경우에 로그 발송
    const myGoodsCountSessionData: Record<string, number> = getZzSessionData(MY_GOODS_COUNT_SESSION_DATA_KEY);
    myGoodsCountSessionData.count = myGoodsCountSessionData.count ? Number(myGoodsCountSessionData.count) + 1 : 1;
    addZzSessionData({ name: MY_GOODS_COUNT_SESSION_DATA_KEY, value: myGoodsCountSessionData });

    if (myGoodsCountSessionData.count >= 3) {
      ZigzagLogEvent.logBraze('add_my_goods', {
        add_my_goods_count: myGoodsCountSessionData.count,
        add_my_goods_count_store: shop.name,
        shop_id: shop.id,
        catalog_product_id: product.id,
      });
    }
  };

  const handleHeartClick = async (isLiked: boolean) => {
    const result = await onInAppLikeProduct({
      like: !isLiked,
      browsingType: PdpBrowsingType.INTERNAL_BROWSER,
      shopId: Number(shop.id),
      shopProductNo: product.external_code,
      catalogProductId: product.id,
      title: product.name,
      goodsUrl: product.product_url,
      imageUrl: url!,
      price: product.product_price.final_discount_info.discount_price,
      discountRate: product.product_price.final_discount_info.discount_rate,
      isZpay: product.payment_type === PdpCatalogPaymentType.ZPAY,
      isFreeShipping: product.shipping_fee?.fee_type === PdpShippingFeeType.FREE,
      ...(!isProductPreview && {
        ...(compareWithAppVersion('7.85.0') && { log: { object_section: 'bottom_bar' } }),
        sendLog: (isLiked) => log.likeProductUBL(isLiked, { catalog_product_id: product.id }),
      }),
    });

    if (isProductPreview) {
      log.likeProductUBL(!isLiked, { catalog_product_id: product.id });
    }

    // MEMO: 서버 레이턴시 이슈로 임시 제거
    // if (catalog_product.entry_type !== PdpProductEntryType.DIRECT) {
    //   if (!SellableFetched) {
    //     Toast.showWithT('common', '잠시 후 다시 이용해주세요.');
    //     return;
    //   }
    //   if (!isExternalProductSellable) {
    //     Toast.showWithT('products', '현재 찜이 불가능한 상품입니다.');
    //     return;
    //   }
    // }

    if (!isLiked) {
      sendSavedProductBrazeLog();
    }

    if (!result) {
      return;
    }

    if (!isProductPreview && result.isSaved) {
      const relatedItemListData = Object.assign(
        {
          scene: 'PRODUCT_DETAIL',
          action: 'LIKE',
          productNo: product.id,
          catalogProductId: product.id,
          imageUrl: url!,
        },
        getBottomRelatedButtonData(),
      );
      ZpayEvent.relatedItemList(relatedItemListData as ZpayRelatedItem);
    }
  };

  return {
    has_review_event: Boolean(data?.simple_banner?.id),
    review_total_count,
    review_prediction_rating,
    isLoading: (enabledPredictionRating && !isPredictSuccessed) || isLoading,
    goToReviewList: () => {
      if (isProductPreview) {
        setProductPreviewActiveTabType(TabType.REVIEW);
      } else {
        path.review.list(catalog_product_id, shop.main_domain);
      }
    },
    onHeartClick: handleHeartClick,
  };
};
